import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { CheckCircle2, FileText } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, FONT_SIZES, TYPOGRAPHY } from '../../theme';
import { AdaptiveSheet, Button, ErrorBanner } from '../common';
import type { ClientStructure } from '../../types/api';
import { useAgentClientsStore } from '../../store/agentClientsStore';
import { getErrorMessage } from '../../utils/errorHandler';
import {
  getReadinessLevelFromClaim,
  getReadinessPercent,
  getReadinessColor,
} from '../../utils/readiness';
import { formatRelativeTime } from '../../utils/time';

interface SendReportSheetProps {
  client: ClientStructure | null;
  onClose: () => void;
}

type Phase = 'confirm' | 'sending' | 'sent';

/**
 * Replaces the one-tap fire-and-forget report send: a confirm step showing
 * what's about to go out and to whom, a pending state, and an explicit
 * success/failure result (the callable's error — including the daily rate
 * limit — used to be swallowed by a `finally`).
 */
export function SendReportSheet({ client, onClose }: SendReportSheetProps) {
  const { colors } = useTheme();
  const checklistsById = useAgentClientsStore((s) => s.checklistsById);
  const sendReport = useAgentClientsStore((s) => s.sendReport);

  const [phase, setPhase] = useState<Phase>('confirm');
  const [error, setError] = useState<string | null>(null);
  const [recipients, setRecipients] = useState<string[]>([]);

  const isOpen = client !== null;

  // Reset per open.
  useEffect(() => {
    if (isOpen) {
      setPhase('confirm');
      setError(null);
      setRecipients([]);
    }
  }, [isOpen, client?.structure.id]);

  const handleSend = async () => {
    if (!client) return;
    setPhase('sending');
    setError(null);
    try {
      const result = await sendReport(client.structure.id);
      setRecipients(result.recipients);
      setPhase('sent');
    } catch (err) {
      setError(getErrorMessage(err));
      setPhase('confirm');
    }
  };

  const structure = client?.structure;
  const claim = client?.claim ?? null;
  const percent = getReadinessPercent(claim);
  const band = getReadinessLevelFromClaim(claim);
  const bandColor = getReadinessColor(band, colors) ?? colors.text;
  const snap = claim?.progressSnapshot;
  const checklistName = claim?.insurerChecklistId
    ? (checklistsById[claim.insurerChecklistId]?.insurerName ?? 'REDI baseline')
    : 'REDI baseline';
  const lastSent = formatRelativeTime(claim?.lastReport?.at, { suffix: 'ago' });

  return (
    <AdaptiveSheet isOpen={isOpen} onRequestClose={onClose} onDismiss={onClose} dialogWidth={480}>
      <AdaptiveSheet.ScrollView contentContainerStyle={styles.content}>
        {phase === 'sent' ? (
          <>
            <View style={styles.resultHeader}>
              <CheckCircle2 size={32} color={colors.progress} />
              <Text style={[styles.title, { color: colors.text }]}>Report sent</Text>
            </View>
            <Text style={[styles.body, { color: colors.textSecondary }]}>
              The readiness report for {structure?.address} was emailed to{' '}
              {recipients.length === 1 ? '1 recipient' : `${recipients.length} recipients`}.
            </Text>
            {recipients.length > 0 && (
              <View
                style={[
                  styles.recipientsBox,
                  { backgroundColor: colors.surface, borderColor: colors.border },
                ]}>
                {recipients.map((email) => (
                  <Text key={email} style={[styles.recipient, { color: colors.textSecondary }]}>
                    {email}
                  </Text>
                ))}
              </View>
            )}
            <Button title="Done" onPress={onClose} fullWidth testID="send-report-done" />
          </>
        ) : (
          <>
            <View style={styles.resultHeader}>
              <FileText size={28} color={colors.primary} />
              <Text style={[styles.title, { color: colors.text }]}>Send readiness report</Text>
            </View>

            <View
              style={[
                styles.summaryBox,
                { backgroundColor: colors.surface, borderColor: colors.border },
              ]}>
              <Text style={[styles.address, { color: colors.text }]}>{structure?.address}</Text>
              <Text style={[styles.city, { color: colors.textSecondary }]}>
                {structure?.city}, {structure?.state} {structure?.zip ?? ''}
              </Text>
              <View style={styles.summaryRow}>
                <Text style={[styles.summaryLabel, { color: colors.textTertiary }]}>Checklist</Text>
                <Text style={[styles.summaryValue, { color: colors.text }]}>{checklistName}</Text>
              </View>
              <View style={styles.summaryRow}>
                <Text style={[styles.summaryLabel, { color: colors.textTertiary }]}>Progress</Text>
                <Text style={[styles.summaryValue, { color: bandColor }]}>
                  {snap && snap.total > 0
                    ? `${snap.completed} of ${snap.total} items (${percent}%)`
                    : 'Not started'}
                </Text>
              </View>
              {lastSent && (
                <View style={styles.summaryRow}>
                  <Text style={[styles.summaryLabel, { color: colors.textTertiary }]}>
                    Last sent
                  </Text>
                  <Text style={[styles.summaryValue, { color: colors.text }]}>{lastSent}</Text>
                </View>
              )}
            </View>

            <Text style={[styles.body, { color: colors.textSecondary }]}>
              The report will be emailed to the property owner, their saved recipients, and your
              email address.
            </Text>

            <ErrorBanner message={error} />

            <Button
              title={phase === 'sending' ? 'Sending...' : 'Send report'}
              onPress={handleSend}
              loading={phase === 'sending'}
              fullWidth
              size="lg"
              testID="send-report-submit"
            />
            <Button title="Cancel" onPress={onClose} variant="ghost" fullWidth />
          </>
        )}
        {/* Scroll sentinel per sheet conventions. */}
        <View testID="send-report-sheet-end" style={styles.scrollSentinel} />
      </AdaptiveSheet.ScrollView>
    </AdaptiveSheet>
  );
}

const styles = StyleSheet.create({
  content: {
    padding: SPACING.lg,
    gap: SPACING.smd,
  },
  resultHeader: {
    alignItems: 'center',
    gap: SPACING.sm,
  },
  title: {
    ...TYPOGRAPHY.h4,
  },
  body: {
    ...TYPOGRAPHY.body,
  },
  summaryBox: {
    borderRadius: BORDER_RADIUS.md,
    borderWidth: 1,
    padding: SPACING.md,
    gap: SPACING.xs,
  },
  address: {
    ...TYPOGRAPHY.h4,
    fontSize: FONT_SIZES.md,
  },
  city: {
    ...TYPOGRAPHY.bodySmall,
    marginBottom: SPACING.xs,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: SPACING.sm,
  },
  summaryLabel: {
    ...TYPOGRAPHY.labelSmall,
  },
  summaryValue: {
    ...TYPOGRAPHY.label,
    flexShrink: 1,
    textAlign: 'right',
  },
  recipientsBox: {
    borderRadius: BORDER_RADIUS.md,
    borderWidth: 1,
    padding: SPACING.md,
    gap: SPACING.xs,
  },
  recipient: {
    ...TYPOGRAPHY.bodySmall,
  },
  scrollSentinel: {
    height: 1,
  },
});
